/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Unless } from "react-if";
import { useLockBodyScroll } from "react-use";
import { Button, Sidebar, Spacer, theme } from "@nordcloud/gnui";
import { Role } from "~/generated/graphql";
import { FiltersFooter } from "~/components";
import { useQueryState } from "~/hooks";
import { FiltersProp } from "~/models/estateRecordRow";
import { useCustomers } from "~/services/customers";
import { getTypedKeys, isEmptyObj } from "~/tools";
import {
  CostAllocationFilters,
  CostAndSavingsFilters,
  ExternalIntegrationsFilters,
  FiltersByResourceAttribute,
} from "./Filters";
import { filterExcludeFilters } from "./helpers";

type Props = {
  isSidebarOpen: boolean;
  filters: FiltersProp;
  hiddenFilters?: (keyof FiltersProp)[];
  toggleIsSidebarOpen: (nextValue?: boolean) => void;
  updateFilters: (filter: FiltersProp) => void;
  toggleFilters: (openFilters: boolean) => void;
  clearFilters: () => void;
};

export function FiltersSidebar({
  isSidebarOpen,
  filters,
  hiddenFilters,
  toggleIsSidebarOpen,
  updateFilters,
  toggleFilters,
  clearFilters,
}: Props) {
  const { updateQueryState, removeFields } = useQueryState();
  const { isAdminLite, userRole } = useCustomers();

  const hasEmptyFilters = isEmptyObj(filters);

  const commit = () => {
    updateQueryState({ ...filterExcludeFilters(filters), page: 0 });
    toggleIsSidebarOpen(false);
  };

  const reset = () => {
    removeFields(filterFields(filters));
    toggleIsSidebarOpen(false);
    clearFilters();
  };

  useLockBodyScroll(isSidebarOpen);

  return (
    <Sidebar
      title="Filters"
      isOpen={isSidebarOpen}
      width="37rem"
      onClick={() => toggleFilters(false)}
    >
      <CostAndSavingsFilters filters={filters} updateFilters={updateFilters} />
      <Unless condition={isAdminLite}>
        <CostAllocationFilters
          filters={filters}
          updateFilters={updateFilters}
        />
      </Unless>
      <FiltersByResourceAttribute
        filters={filters}
        updateFilters={updateFilters}
        hiddenFilters={hiddenFilters}
      />
      <Unless condition={userRole === Role.TeamLead}>
        <ExternalIntegrationsFilters
          filters={filters}
          updateFilters={updateFilters}
        />
      </Unless>
      <Spacer
        height={theme.spacing.spacing04}
        css={{ marginTop: theme.spacing.spacing04 }}
      />
      <FiltersFooter>
        <Button
          disabled={hasEmptyFilters}
          mr={theme.spacing.spacing02}
          onClick={commit}
        >
          Apply filters
        </Button>
        <Button severity="low" disabled={hasEmptyFilters} onClick={reset}>
          Clear filters &amp; reload
        </Button>
      </FiltersFooter>
    </Sidebar>
  );
}

function filterFields(filters: FiltersProp) {
  const fieldsToKeep = ["estatePeriod", "periodStart", "periodEnd", "groupBy"];

  const allFields = [...getTypedKeys(filters)];
  return [...allFields.filter((key) => !fieldsToKeep.includes(key)), "page"];
}
